import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

import { promotionKeys } from "./query-keys";

const EMPTY_MAP: Record<string, string> = {};

/**
 * Resolve a set of SKU codes to their product names (`{ [sku]: name }`) via
 * POST /v1/skus/resolve. The query counterpart to the picker's `useResolveSkus`
 * mutation: this one is driven reactively by whatever SKUs a RuleCard currently
 * holds, so trigger/gift chips can show "SKU – Name" for both freshly-picked and
 * hydrated (saved) SKUs. Unrecognized codes are simply absent from the map
 * (callers fall back to the bare SKU). Returns {} while empty or loading.
 *
 * The codes are de-duplicated and sorted so the React-Query key is stable
 * regardless of order/dupes (identical sets share one cache entry).
 */
export function useResolveSkuNames(codes: string[]): Record<string, string> {
  const unique = Array.from(new Set(codes)).sort();
  const { data } = useQuery({
    queryKey: promotionKeys.skuResolve(unique),
    enabled: unique.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await apiClient.POST("/v1/skus/resolve", {
        body: { codes: unique },
      });
      /* v8 ignore next -- unreachable: client errorMiddleware throws ApiError on !ok */
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const row of data?.resolved ?? []) map[row.sku] = row.name;
      return map;
    },
    staleTime: 30_000,
  });
  return data ?? EMPTY_MAP;
}
